// Lightweight shadcn/ui-style primitives (Tailwind). Accessible, self-contained (NFR-7).
import { clsx } from "clsx";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

export function Button({
  variant = "primary",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger" }): JSX.Element {
  const styles: Record<string, string> = {
    primary: "bg-brand text-white hover:bg-blue-700 disabled:opacity-50",
    secondary: "bg-white border border-slate-300 text-slate-800 hover:bg-slate-100 disabled:opacity-50",
    ghost: "text-slate-700 hover:bg-slate-100",
    danger: "bg-red-600 text-white hover:bg-red-700 disabled:opacity-50",
  };
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
        styles[variant],
        className,
      )}
      {...props}
    />
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={clsx("rounded-lg border border-slate-200 bg-white shadow-sm", className)}>{children}</div>;
}

export function CardHeader({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={clsx("border-b border-slate-100 px-4 py-3", className)}>{children}</div>;
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={clsx("px-4 py-3", className)}>{children}</div>;
}

const BADGE_TONES: Record<string, string> = {
  slate: "bg-slate-100 text-slate-700",
  blue: "bg-blue-100 text-blue-800",
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  violet: "bg-violet-100 text-violet-800",
};

export function Badge({ children, tone = "slate" }: { children: ReactNode; tone?: keyof typeof BADGE_TONES }): JSX.Element {
  return (
    <span className={clsx("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", BADGE_TONES[tone])}>
      {children}
    </span>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <input
      className={clsx(
        "w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return (
    <textarea
      className={clsx(
        "w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
        className,
      )}
      rows={2}
      {...props}
    />
  );
}

/** 1–5 star rating input (matches the Softr claim-rating widgets). 0 = unrated. Keyboard-accessible. */
export function StarRating({
  value,
  onChange,
  readOnly = false,
  label = "rating",
}: {
  value: number;
  onChange?: (n: number) => void;
  readOnly?: boolean;
  label?: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          aria-checked={value === n}
          role="radio"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(value === n ? 0 : n)}
          className={clsx(
            n <= value ? "text-amber-500" : "text-slate-300",
            !readOnly && "hover:text-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded",
            readOnly && "cursor-default",
          )}
        >
          <span className="text-xl leading-none">★</span>
        </button>
      ))}
      {value > 0 && <span className="ml-1 text-xs text-slate-500">{value}/5</span>}
    </div>
  );
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return (
    <select
      className={clsx(
        "rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

export function Spinner({ label }: { label?: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-500" role="status" aria-live="polite">
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-brand" aria-hidden />
      {label}
    </span>
  );
}

export function EmptyState({ children }: { children: ReactNode }): JSX.Element {
  return <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">{children}</div>;
}

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close">✕</Button>
        </div>
        <div className="px-4 py-4">{children}</div>
      </div>
    </div>
  );
}

/** Map a reviewer/preprint status to a badge tone. */
export function statusTone(status: string): keyof typeof BADGE_TONES {
  if (status === "Unread" || status === "Taken") return "green";
  if (/Invite$|^Invite$|Fixed|COMPLETE|Selected|Published/.test(status)) return "green";
  if (/Reject|DISCARDED|COI Detected/.test(status)) return "red";
  if (/Fixable|Double Check|ON HOLD|Checking/.test(status)) return "amber";
  if (/ACTIVE|INVITED|Invite Backup/.test(status)) return "blue";
  return "slate";
}
